import type { Feature } from '@hotjar/webapp/domains/common/feature/types';
import { getLocalOverride } from '@hotjar/webapp/domains/common/feature/local-override';
import { includes } from 'lodash-es';
import type { Site } from '@hotjar/webapp/domains/common/feature';
import * as UserData from '@hotjar/webapp/domains/common/user-data';
import { get as getSettings } from '@hotjar/webapp/domains/common/settings';

/**
 * This file contains feature flag and feature gating related functions dedicated
 * for usage within the Feedback package (Ask module).
 * For users with feedback_effective_plan
 * the Ask plan will be used as a reference for plan-based flags lookup.
 * It it does not exist, the standard effective plan will be used as a fall back.
 * */

/*
 * Returns the list of all features
 */
export function getAllForAsk(site: Site | null): string[] {
  if (!site || !UserData.getLoggedIn()) {
    return [];
  }

  let plan;
  // If feedback (Ask module) plan is defined, use it for features look up
  // otherwise - fall back to standard effective plan (for a single SKU user)
  if (site.effectiveAskPlan || site.effective_plan_for_feedback) {
    plan = site.effective_plan_for_feedback
      ? site.effective_plan_for_feedback
      : site.effectiveAskPlan;
  } else {
    plan = site.effective_plan ? site.effective_plan : site.effectivePlan;
  }

  // Return early if we don't have the plan (e.g. super outdated UserData cached in the browser).
  if (!plan) {
    return [];
  }

  const settings = getSettings();
  const planSettings = settings ? settings.plans[plan].features : [];

  return planSettings.concat(
    site.features || [],
    site.organization.features || [],
    site.account.features || [],
  );
}

/**
 * Check if a feature can be used by a specific site.
 */
export function isAllowedForAsk(feature: Feature, site: Site | null): boolean {
  const localStorageOverride = getLocalOverride(feature);

  if (localStorageOverride !== null) {
    return localStorageOverride;
  }

  const features = getAllForAsk(site);

  return includes(features, feature);
}
